import { ref } from 'vue'

export interface Note {
  id: string
  phone_number: string
  title: string
  content: string
  agent_id: string
  created_at: string
  updated_at: string
}

export interface ContactsEZdial{
  cont_nombre: string
  cont_telefono: string
  llamar: string
  marcado: boolean
  [key: string]: string | boolean
}

const DB_NAME = 'hub_db'
const DB_VERSION = 2

export const STORES = {
  NOTES: 'notes',
  CONTACTS_EZDIAL: 'contacts_ezdial',
} as const

export function useIndexedDB() {
  const db = ref<IDBDatabase | null>(null)
  const isReady = ref(false)

  const initDB = () =>
    new Promise<void>((resolve, reject) => {
      if (db.value) { resolve(); return }

      const req = indexedDB.open(DB_NAME, DB_VERSION)

      req.onerror = () => reject(req.error)
      req.onsuccess = () => {
        db.value = req.result
        isReady.value = true
        resolve()
      }

      req.onupgradeneeded = () => {
        const dbInstance = req.result

        // Notas
        if (!dbInstance.objectStoreNames.contains(STORES.NOTES)) {
          const noteStore = dbInstance.createObjectStore(STORES.NOTES, { keyPath: 'id' })
          noteStore.createIndex('idx_title', 'title', { unique: false })
        }
        // Contacts EZdial
        if (!dbInstance.objectStoreNames.contains(STORES.CONTACTS_EZDIAL)) {
          const contactStore = dbInstance.createObjectStore(STORES.CONTACTS_EZDIAL, { keyPath: 'cont_telefono' })
          contactStore.createIndex('idx_cont_nombre', 'cont_nombre', { unique: false })
        }
      }
    })

  // ===== Notas =====
  const getAllNotes = (): Promise<Note[]> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.NOTES, 'readonly')
      const req = tx.objectStore(STORES.NOTES).getAll()
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })

  const getNoteById = (id: string): Promise<Note | undefined> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.NOTES, 'readonly')
      const req = tx.objectStore(STORES.NOTES).get(id)
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })

  const addNote = (note: Omit<Note, 'id' | 'created_at' | 'updated_at'>): Promise<string> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const now = new Date().toISOString()
      const full: Note = {
        ...note,
        id: crypto.randomUUID(),
        created_at: now,
        updated_at: now,
      }
      const tx = db.value.transaction(STORES.NOTES, 'readwrite')
      const req = tx.objectStore(STORES.NOTES).add(full)
      req.onsuccess = () => resolve(full.id)
      req.onerror = () => reject(req.error)
    })

  const updateNote = (note: Note): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.NOTES, 'readwrite')
      const req = tx.objectStore(STORES.NOTES).put({ ...note, updated_at: new Date().toISOString() })
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })

  const removeNote = (id: string): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.NOTES, 'readwrite')
      const req = tx.objectStore(STORES.NOTES).delete(id)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })

  // ===== Contacts EZdial =====
  const getAllContactsEZdial = (): Promise<ContactsEZdial[]> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')

      const req = db.value
        .transaction(STORES.CONTACTS_EZDIAL, 'readonly')
        .objectStore(STORES.CONTACTS_EZDIAL)
        .getAll()

      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })

  const addContactsEZdial = (contacts: ContactsEZdial[]): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')

      const tx = db.value.transaction(STORES.CONTACTS_EZDIAL, 'readwrite')
      const store = tx.objectStore(STORES.CONTACTS_EZDIAL)

      contacts.forEach(contact => {
        store.put({
          ...contact,
          marcado: contact.marcado ?? false,
        })
      })

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })

  const markContactAsCalled = (cont_telefono: string): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')

      const tx = db.value.transaction(STORES.CONTACTS_EZDIAL, 'readwrite')
      const store = tx.objectStore(STORES.CONTACTS_EZDIAL)

      const req = store.get(cont_telefono)

      req.onsuccess = () => {
        const contact = req.result as ContactsEZdial | undefined
        if (!contact) {
          resolve()
          return
        }

        store.put({
          ...contact,
          marcado: true,
        })
      }

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })

  const clearContactsEZdial = (): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')

      const req = db.value
        .transaction(STORES.CONTACTS_EZDIAL, 'readwrite')
        .objectStore(STORES.CONTACTS_EZDIAL)
        .clear()

      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })

  const removeContactEZdial = (cont_telefono: string): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')

      const req = db.value
        .transaction(STORES.CONTACTS_EZDIAL, 'readwrite')
        .objectStore(STORES.CONTACTS_EZDIAL)
        .delete(cont_telefono)

      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })


  return {
    initDB,
    isReady,
    getAllNotes,
    getNoteById,
    addNote,
    updateNote,
    removeNote,
    getAllContactsEZdial,
    addContactsEZdial,
    markContactAsCalled,
    clearContactsEZdial,
    removeContactEZdial,
  }
}
